'use client';

import { deleteLineItem } from '@lib/data/cart';
import { cn } from '@lib/util/cn';
import { Button } from '@modules/common/components/button';
import { CART_CHANGED_EVENT } from '@modules/common/components/cart-provider/constants';
import { Trash } from '@modules/common/icons';
import { useState } from 'react';

type DeleteButtonProps = React.ComponentPropsWithoutRef<'button'> & {
  id: string;
  onDelete?: () => void;
};

const DeleteButton = ({
  id,
  children,
  className,
  onDelete,
  ...buttonProps
}: DeleteButtonProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await deleteLineItem(id);
      window.dispatchEvent(new Event('medusa:cartitemremoved'));
      window.dispatchEvent(new Event(CART_CHANGED_EVENT));
      onDelete?.();
    } catch (_err) {
      // reset so the button isn't stuck in loading state
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between typography-paragraph-xs',
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        aria-label={children ? undefined : 'Remove item'}
        isLoading={isDeleting}
        className="h-auto py-0 px-0 gap-x-1 text-ui-fg-base hover:text-ui-fg-subtle hover:bg-transparent disabled:cursor-wait disabled:opacity-100"
        onClick={() => handleDelete(id)}
        {...buttonProps}
      >
        <Trash aria-hidden="true" />
        <span>{children}</span>
      </Button>
      <span role="status" aria-live="polite" className="sr-only">
        {isDeleting ? 'Removing item…' : ''}
      </span>
    </div>
  );
};

export default DeleteButton;
